import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { motion } from "framer-motion";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Mockup components for each feature section
function CaptureZoneMockup() {
  return (
    <div className="bg-card rounded-xl border border-border shadow-lg p-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="h-3 w-32 bg-muted rounded" />
        <div className="bg-muted/50 rounded-lg p-4 border border-border">
          <div className="h-4 w-full bg-primary/20 rounded mb-2" />
          <div className="h-4 w-3/4 bg-primary/10 rounded" />
        </div>
        <div className="flex justify-end">
          <div className="h-9 w-28 bg-primary rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function StructuredViewMockup() {
  return (
    <div className="bg-card rounded-xl border border-border shadow-lg p-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-primary/20 rounded-full" />
          <div className="h-4 w-40 bg-muted rounded" />
        </div>
        <div className="h-px bg-border" />
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="h-3 w-16 bg-muted-foreground/30 rounded" />
            <div className="h-3 w-48 bg-muted rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-3 w-16 bg-muted-foreground/30 rounded" />
            <div className="h-3 w-40 bg-muted rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-3 w-16 bg-muted-foreground/30 rounded" />
            <div className="h-3 w-52 bg-muted rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-3 w-16 bg-muted-foreground/30 rounded" />
            <div className="h-3 w-36 bg-muted rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

function GrowthTrackingMockup() {
  return (
    <div className="bg-card rounded-xl border border-border shadow-lg p-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-4 w-28 bg-muted rounded" />
          <div className="flex gap-1">
            <div className="h-2 w-2 rounded-full bg-primary" />
            <div className="h-2 w-2 rounded-full bg-primary" />
            <div className="h-2 w-2 rounded-full bg-primary/40" />
            <div className="h-2 w-2 rounded-full bg-muted" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {["Seed", "Sprout", "Plant", "Tree"].map((stage, i) => (
            <div key={stage} className="text-center">
              <div className={`h-10 w-10 mx-auto rounded-full ${i < 2 ? "bg-primary/20" : "bg-muted"} flex items-center justify-center`}>
                <div className={`h-4 w-4 rounded-full ${i < 2 ? "bg-primary" : "bg-muted-foreground/30"}`} />
              </div>
              <div className="h-2 w-10 mx-auto bg-muted rounded mt-2" />
            </div>
          ))}
        </div>
        <div className="h-px bg-border" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-primary/10 rounded" />
          <div className="flex-1">
            <div className="h-3 w-24 bg-muted rounded mb-1" />
            <div className="h-2 w-32 bg-muted-foreground/20 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

function InstrumentsMockup() {
  return (
    <div className="bg-card rounded-xl border border-border shadow-lg p-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="h-4 w-36 bg-muted rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="h-8 w-8 bg-primary/20 rounded mb-2" />
            <div className="h-3 w-20 bg-muted rounded mb-1" />
            <div className="h-2 w-16 bg-muted-foreground/20 rounded" />
          </div>
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="h-8 w-8 bg-primary/20 rounded mb-2" />
            <div className="h-3 w-20 bg-muted rounded mb-1" />
            <div className="h-2 w-16 bg-muted-foreground/20 rounded" />
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 border border-border">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-primary/30 rounded" />
            <div className="h-3 w-32 bg-muted rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

const features = [
  {
    headline: "Capture ideas before they dissolve",
    description: "A calm, always-ready input zone for thoughts, fragments, and half-formed ideas. No friction, no formatting — just capture.",
    mockup: <CaptureZoneMockup />
  },
  {
    headline: "Force clarity through structure",
    description: "Every idea gets structured automatically: problem, audience, solution, differentiator. Structure reveals whether an idea has substance or was just noise.",
    mockup: <StructuredViewMockup />
  },
  {
    headline: "Track growth over time",
    description: "Ideas progress through stages — seed, sprout, plant, tree. Return to water them with reflection. Ideas that aren't nurtured will wither. Mature ideas become stable.",
    mockup: <GrowthTrackingMockup />
  },
  {
    headline: "Expand with growth instruments",
    description: "When an idea is ready to grow, use focused tools to analyze competitors, find related products, and plan execution. Research that serves the idea, not busywork.",
    mockup: <InstrumentsMockup />
  }
];

export default function Features() {
  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />
      
      <main className="pt-24 pb-20">
        {/* Page header */}
        <section className="max-w-4xl mx-auto px-6 text-center mb-20">
          <motion.h1 
            className="text-3xl md:text-4xl font-medium text-foreground mb-4"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.5 }}
          >
            What YELV helps you do
          </motion.h1>
          <motion.p 
            className="text-lg text-muted-foreground max-w-xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            A structured system for capturing, clarifying, and growing ideas — 
            from fleeting thought to stable direction.
          </motion.p>
        </section>

        {/* Feature sections */}
        <div className="max-w-6xl mx-auto px-6 space-y-24 md:space-y-32">
          {features.map((feature, index) => (
            <motion.section 
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-10 md:gap-16`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex-1 space-y-4">
                <h2 className="text-2xl md:text-3xl font-medium text-foreground">
                  {feature.headline}
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {feature.description}
                </p>
              </div>
              <div className="flex-1 flex justify-center">
                {feature.mockup}
              </div>
            </motion.section>
          ))}
        </div>
      </main>
      
      <MarketingFooter />
    </div>
  );
}
